import { Month } from './Month';
import { defaults, classes } from './defaults';
import { first } from './utils/array';
import { setDeep } from './utils/object';
import { createElement } from './utils/dom';
import { getMonths } from './utils/getMonths';
import { addDays, addMonths, isSameDay, subDays, subMonths, startOfDay, isWithinInterval } from 'date-fns';

export function DatePicker(options) {
	let config = {
		...defaults,
		...options,
		classes: { ...classes, ...options.classes },
	};

	config.date = startOfDay(config.date);
	config.minDate = startOfDay(config.minDate);
	config.maxDate = startOfDay(config.maxDate);

	let state = {
		days: [],
		months: [],
		focusedDay: undefined,
	};

	let monthsElement = createElement('div');
	let { prevMonthButton, nextMonthButton } = createUI();

	monthsElement.className = config.classes.months;

	prevMonthButton.addEventListener('click', () => prev());
	nextMonthButton.addEventListener('click', () => next());

	config.container.classList.add(config.classes.root);
	config.container.append(prevMonthButton, nextMonthButton, monthsElement);

	render();
	registerEventListeners();

	function render(date = config.date) {
		state.days = [];
		state.months = [];

		monthsElement.innerHTML = '';

		let months = getMonths(date, config);

		months.map(month => {
			let { render, days } = Month(month, config);

			state.days.push(...days);
			state.months.push(month);

			monthsElement.append(render());
		});

		config.date = date;

		if (state.focusedDay) {
			state.focusedDay.focus();
		}
	}

	function goTo(date) {
		render(date);
	}

	function next(step = 1) {
		let { pagedNavigation, numberOfMonths } = config;
		let n = pagedNavigation ? numberOfMonths : step;

		let date = addMonths(first(state.months), n);

		render(date);
		makeFirstFocusable();
	}

	function prev(step = 1) {
		let { pagedNavigation, numberOfMonths } = config;
		let n = pagedNavigation ? numberOfMonths : step;

		let date = subMonths(first(state.months), n);

		render(date);
		makeFirstFocusable();
	}

	function registerEventListeners() {
		config.container.addEventListener('dayClick', handleDayClick);
		config.container.addEventListener('dayFocus', handleDayFocus);
		config.container.addEventListener('dayKeydown', handleDayKeydown);
	}

	function handleDayClick(event) {
		event.detail.focus();
	}

	function handleDayFocus(event) {
		if (state.focusedDay && (state.focusedDay.element !== event.detail.element)) {
			state.focusedDay.blur();
		}

		state.focusedDay = event.detail;
	}

	function handleDayKeydown(event) {
		let handled = false;
		let { date, event: { key } } = event.detail;

		switch (key) {
			case 'ArrowLeft':
				handled = true;
				focus(subDays(date, 1));
				break;
			case 'ArrowRight':
				handled = true;
				focus(addDays(date, 1));
				break;
			case 'ArrowUp':
				handled = true;
				focus(subDays(date, 7));
				break;
			case 'ArrowDown':
				handled = true;
				focus(addDays(date, 7));
				break;
		}

		if (handled) {
			event.preventDefault();
		}

		function focus(date) {
			let { minDate, maxDate } = config;
			let days = state.days.filter(isFocusable);
			let day = days.find(day => isSameDay(day.date, date));

			if (day) {
				return day.focus();
			}

			if (isWithinInterval(date, { start: minDate, end: maxDate })) {
				render(date);
				focus(date);
			}

			function isFocusable(day) {
				return !day.modifiers.outside && !day.modifiers.hidden;
			}
		}
	}

	function makeFirstFocusable() {
		let days = state.days.filter(isFocusable);

		if (days.length) {
			days[0].enableKeyboardFocus();
		}

		function isFocusable(day) {
			return !day.modifiers.outside && !day.modifiers.hidden;
		}
	}

	function set(key, value, refresh = true) {
		config = setDeep(config, key, value);

		if (refresh) {
			render();
		}
	}

	return {
		goTo,
		next,
		prev,
		set,
		config,
		render,
	};
}

function createUI() {
	const nextMonthButton = createElement('button');
	const prevMonthButton = createElement('button');

	nextMonthButton.classList.add('datepicker-next');
	prevMonthButton.classList.add('datepicker-prev');

	nextMonthButton.setAttribute('type', 'button');
	prevMonthButton.setAttribute('type', 'button');

	nextMonthButton.setAttribute('aria-label', 'Next month');
	prevMonthButton.setAttribute('aria-label', 'Previous month');

	return {
		prevMonthButton,
		nextMonthButton,
	};
}
